import React, { useMemo, useState, useEffect } from "react";
import { HiOutlineChevronDown, HiOutlineChevronRight } from "react-icons/hi";

const styles = `
  .uc-index {
    background: linear-gradient(180deg, #2fb7a4 0%, #1c3f7b 100%);
    padding: 48px 35px 56px;
  }
  .uc-index-inner {
    max-width: 1100px;
    margin: 0 auto;
  }
  .uc-index-top {
    position: relative;
    margin-bottom: 28px;
  }
  .uc-index-head {
    text-align: center;
    padding-right: 0;
  }
  .uc-index-head h2 {
    font-family: Exo, sans-serif;
    font-size: 34px;
    font-weight: 700;
    color: #fff;
    margin: 0 0 10px;
    line-height: 1.15;
  }
  .uc-index-head p {
    margin: 0 auto;
    max-width: 640px;
    font-size: 15px;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.9);
  }
  .uc-index-filter-bar {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 2;
  }
  .uc-index-select-wrap {
    position: relative;
    display: inline-block;
  }
  .uc-index-select-wrap svg {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #27baa7;
    font-size: 16px;
    pointer-events: none;
  }
  .uc-index-select {
    appearance: none;
    -webkit-appearance: none;
    min-width: 210px;
    max-width: 260px;
    border: 1px solid rgba(39, 186, 167, 0.55);
    border-radius: 8px;
    background: rgba(29, 63, 125, 0.72);
    color: #fff;
    font-family: Roboto, sans-serif;
    font-size: 13px;
    font-weight: 500;
    padding: 9px 34px 9px 12px;
    cursor: pointer;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
    transition: border-color 0.2s ease, background 0.2s ease;
  }
  .uc-index-select:hover {
    border-color: #27baa7;
    background: rgba(29, 63, 125, 0.88);
  }
  .uc-index-select:focus {
    border-color: #27baa7;
    background: rgba(29, 63, 125, 0.88);
    outline: 2px solid rgba(39, 186, 167, 0.35);
    outline-offset: 2px;
  }
  .uc-index-select option {
    background: #1d3f7d;
    color: #fff;
  }
  .uc-index-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(248px, 1fr));
    gap: 14px;
  }
  .uc-index-card {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 76px;
    padding: 12px 14px;
    background: rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 10px;
    text-decoration: none;
    color: inherit;
    box-sizing: border-box;
    transition: background 0.25s ease, transform 0.25s ease, border-color 0.25s ease;
  }
  .uc-index-card:hover,
  .uc-index-card:focus {
    background: rgba(0, 0, 0, 0.32);
    border-color: rgba(255, 255, 255, 0.28);
    transform: translateY(-2px);
    outline: none;
  }
  .uc-index-badge {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: rgba(0, 0, 0, 0.28);
    color: #fff;
    font-family: Exo, sans-serif;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.02em;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    line-height: 1.1;
    padding: 4px;
  }
  .uc-index-body {
    flex: 1;
    min-width: 0;
  }
  .uc-index-body strong {
    display: block;
    font-family: Exo, sans-serif;
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    line-height: 1.35;
    margin-bottom: 3px;
  }
  .uc-index-body span {
    display: block;
    font-size: 11px;
    color: rgba(255, 255, 255, 0.72);
    line-height: 1.25;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .uc-index-arrow {
    flex-shrink: 0;
    color: rgba(255, 255, 255, 0.85);
    font-size: 18px;
  }
  .uc-index-card.is-hidden {
    display: none;
  }
  .uc-index-empty {
    grid-column: 1 / -1;
    text-align: center;
    color: rgba(255, 255, 255, 0.85);
    font-size: 14px;
    padding: 24px;
  }
  @media (max-width: 768px) {
    .uc-index { padding: 36px 20px 44px; }
    .uc-index-head h2 { font-size: 26px; }
    .uc-index-top { padding-top: 0; }
    .uc-index-filter-bar {
      position: static;
      display: flex;
      justify-content: flex-end;
      margin-bottom: 18px;
    }
    .uc-index-select {
      min-width: 0;
      width: 100%;
      max-width: 100%;
    }
    .uc-index-grid { grid-template-columns: 1fr; }
  }
  @media (max-width: 900px) {
    .uc-index-head {
      padding-top: 44px;
    }
  }
`;

function initials(label) {
  if (!label) return "AI";
  const words = label.replace(/&/g, " ").split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 3).toUpperCase();
  return words
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

function scrollToUseCase(event, id) {
  event.preventDefault();
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  const hash = `#${id}`;
  if (window.location.hash !== hash) {
    window.history.replaceState(null, "", hash);
  }
  el.setAttribute("tabindex", "-1");
  el.focus({ preventScroll: true });
}

export default function UseCaseIndexGrid({
  useCases,
  groups,
  heading,
  subtitle,
  onFilterChange,
}) {
  const [activeGroup, setActiveGroup] = useState("all");

  const handleFilterChange = (value) => {
    setActiveGroup(value);
    onFilterChange?.(value);
  };

  const showEmpty =
    activeGroup !== "all" &&
    !useCases.some((uc) => uc.groupId === activeGroup);

  const positionById = useMemo(() => {
    const map = new Map();
    useCases.forEach((uc, index) => map.set(uc.id, index + 1));
    return map;
  }, [useCases]);

  const showFilters = (groups?.length ?? 0) > 1;

  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, "");
    if (!hash || !useCases.some((uc) => uc.id === hash)) return;
    const el = document.getElementById(hash);
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [useCases]);

  return (
    <nav
      className="uc-index"
      aria-label={heading}
      itemScope
      itemType="https://schema.org/ItemList"
    >
      <style>{styles}</style>
      <div className="uc-index-inner">
        <div className="uc-index-top">
          {showFilters ? (
            <div className="uc-index-filter-bar">
              <div className="uc-index-select-wrap">
                <select
                  id="uc-group-filter"
                  className="uc-index-select"
                  value={activeGroup}
                  onChange={(e) => handleFilterChange(e.target.value)}
                  aria-label="Browse by category"
                >
                  <option value="all">All use cases ({useCases.length})</option>
                  {groups.map((group) => {
                    const count = useCases.filter((uc) => uc.groupId === group.id).length;
                    if (!count) return null;
                    return (
                      <option key={group.id} value={group.id}>
                        {group.label}
                        {count > 1 ? ` (${count})` : ""}
                      </option>
                    );
                  })}
                </select>
                <HiOutlineChevronDown aria-hidden="true" />
              </div>
            </div>
          ) : null}

          <header className="uc-index-head">
            <h2 itemProp="name">{heading}</h2>
            {subtitle ? <p>{subtitle}</p> : null}
          </header>
        </div>

        <div className="uc-index-grid">
          {showEmpty ? (
            <p className="uc-index-empty">No use cases in this category.</p>
          ) : (
            useCases.map((uc) => {
              const hidden = activeGroup !== "all" && uc.groupId !== activeGroup;
              const anchorHref = `#${uc.id}`;
              return (
                <a
                  key={uc.id}
                  href={anchorHref}
                  className={`uc-index-card${hidden ? " is-hidden" : ""}`}
                  onClick={(e) => scrollToUseCase(e, uc.id)}
                  itemProp="itemListElement"
                  itemScope
                  itemType="https://schema.org/ListItem"
                  aria-label={`Jump to ${uc.title} use case`}
                  aria-hidden={hidden || undefined}
                  tabIndex={hidden ? -1 : undefined}
                >
                  <meta itemProp="position" content={String(positionById.get(uc.id))} />
                  <span className="uc-index-badge" aria-hidden="true">
                    {initials(uc.subIndustry)}
                  </span>
                  <span className="uc-index-body">
                    <strong itemProp="name">{uc.title}</strong>
                  </span>
                  <HiOutlineChevronRight className="uc-index-arrow" aria-hidden="true" />
                  {uc.canonicalUrl ? (
                    <link itemProp="url" href={uc.canonicalUrl} />
                  ) : (
                    <link itemProp="url" href={anchorHref} />
                  )}
                </a>
              );
            })
          )}
        </div>
      </div>
    </nav>
  );
}

